import { ChangeEvent, useRef, useState } from "react"
import { User } from "domain/model/User"
import { STR_FAILURE, STR_FILL_ALL_FIELDS, STR_LOGIN_SUCCESSFUL, STR_SUCCESS } from "core/constants/strings"
import { useNavigate } from "react-router-dom"
import { isEmail } from "core/utils/validator"
import { isEmpty } from "core/utils/misc"
import { useAuth } from "core/context/auth"
import useToast from "core/hooks/useToast"
import { redirectPath } from "core/lib/utils"
import delay from "core/utils/delay"

export default function LoginViewModel() {
  const [user, setUser] = useState<User>({ email: "", password: "" })
  const [loading, setLoading] = useState<boolean>(false)
  const [emailValidity, setEmailValidity] = useState({ status: false, message: "" })

  const inputRef = useRef<HTMLInputElement>(null)

  const navigate = useNavigate()

  const { toast, changeToastDetails, changeToastVisibility } = useToast()
  const { auth, login } = useAuth()

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newUser = { ...user, [e.target.name]: e.target.value }
    setUser(newUser)
  }

  const checkEmailValidity = () => {
    const isValid = isEmail(user.email)
    setEmailValidity({
      status: isValid,
      message: isValid ? "Good to go!" : "Sorry, there might be something wrong!",
    })
  }

  const handleEmailBlur = () => {
    checkEmailValidity()
  }

  const handleLogin = async (e: any) => {
    e.preventDefault()

    if (process.env.REACT_APP_ENV !== "development") {
      if (isEmpty(user.email) || isEmpty(user.password)) {
        changeToastVisibility(true)
        changeToastDetails(STR_FAILURE, STR_FILL_ALL_FIELDS)
        return
      }
    }

    if (isEmpty(user.email)) {
      changeToastVisibility(true)
      changeToastDetails(STR_FAILURE, "Email Missing")
      return
    }

    if (!isEmail(user.email)) return

    const email = user.email.trim().toLowerCase()
    const password = user.password.trim()

    setLoading(true)

    try {
      await login(email, password)

      changeToastVisibility(true)
      changeToastDetails(STR_SUCCESS, STR_LOGIN_SUCCESSFUL)
    } catch (e: unknown) {
      let toastMessage = "Unknown Error Occurred"

      if (e instanceof Error) {
        const { message } = e
        toastMessage = message

        if (message.includes("wrong")) {
          toastMessage = "Wrong Password"
        } else if (message.includes("too-many-requests")) {
          toastMessage = "Due to too many wrong attempts, your account is temporarily disabled. Try again after 5 mins."
        } else if (message.includes("user-not-found")) {
          toastMessage = "User Not Found"
        }
      }

      changeToastVisibility(true)
      changeToastDetails(STR_FAILURE, toastMessage)
    } finally {
      setLoading(false)
    }
  }

  return {
    auth,
    user,
    toast,
    loading,
    emailValidity,
    inputRef,
    handleInputChange,
    handleLogin,
    changeToastVisibility,
    changeToastDetails,
    redirectToPath: redirectPath,
    handleEmailBlur,
    navigate,
  }
}
